import * as React from 'react';
import { Box, Button, Divider, Drawer, IconButton, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchableDropdown, {
  type SearchableDropdownOption,
} from 'components/Dropdown/SearchableDropdown';

type FieldType = 'dropdown';

export type ReusableConfigField = {
  key: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  options?: SearchableDropdownOption[];
  noOptionsText?: string;
  width?: string | number;
};

export type ReusableConfigPanelValues = Record<string, SearchableDropdownOption | null>;

type ReusableConfigPanelProps = {
  open: boolean;
  title: string;
  fields: ReusableConfigField[];
  values: ReusableConfigPanelValues;
  onFieldChange: (fieldKey: string, value: SearchableDropdownOption | null) => void;
  onClose: () => void;
  onCancel?: () => void;
  onSave: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  saveDisabled?: boolean;
  loading?: boolean;
  width?: number | string;
};

const ReusableConfigPanel = ({
  open,
  title,
  fields,
  values,
  onFieldChange,
  onClose,
  onCancel,
  onSave,
  saveLabel = 'SAVE',
  cancelLabel = 'CANCEL',
  saveDisabled = false,
  loading = false,
  width = 390,
}: ReusableConfigPanelProps) => {
  const theme = useTheme();
  const colors = theme.palette;

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width,
          maxWidth: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: `${colors.modal.background} !important`,
          boxShadow: '0px 2px 8px rgba(16, 24, 40, 0.08), -8px 0px 24px rgba(16, 24, 40, 0.12)',
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="widgetTitle"
          sx={{
            color: colors.modal.subTitle,
            pr: 2,
          }}
        >
          {title}
        </Typography>

        <IconButton
          onClick={onClose}
          aria-label="close-panel"
          sx={{
            p: 0.5,
          }}
        >
          <CloseIcon sx={{ color: colors.modal.subTitle }} />
        </IconButton>
      </Box>

      <Divider
        sx={{
          borderColor: colors.modal.subTitle,
          mx: 2,
        }}
      />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fields.map((field) => {
            if (field.type === 'dropdown') {
              return (
                <SearchableDropdown
                  key={field.key}
                  label={field.label}
                  required={field.required}
                  disabled={field.disabled}
                  value={values[field.key] ?? null}
                  setValue={(newValue: SearchableDropdownOption | null) =>
                    onFieldChange(field.key, newValue)
                  }
                  options={field.options ?? []}
                  placeholder={field.placeholder}
                  noOptionsText={field.noOptionsText}
                  width={field.width ?? '100%'}
                  handleChange={(selected) => {
                    onFieldChange(field.key, selected);
                  }}
                />
              );
            }

            return null;
          })}
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <Button
            variant="pepwiseButtonText"
            onClick={handleCancel}
            sx={{
              minWidth: 100,
              height: 40,
              color: colors.buttons.primary.color,
            }}
          >
            {cancelLabel}
          </Button>

          <Button
            variant="pepwiseSecondary"
            onClick={onSave}
            disabled={saveDisabled || loading}
            sx={{
              minWidth: 120,
              height: 40,
              borderRadius: '4px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: colors.buttons.primary.bgColor,
                boxShadow: 'none',
              },
            }}
          >
            {saveLabel}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ReusableConfigPanel;
